<template>

  <div class="container mt-3">

    <div v-if="status === 'view-all'">

      <div class="desktop-view">
        <div class="search-container mb-2">

          <button
          type="button"
            class="btn btn-primary btn-sm ms-2 mb-2 mb-sm-0"
            data-tippy-content="create new enterprise"
            @click="changeStatus('create')"
            title="add new enterprise"
><font-awesome-icon icon="plus" /></button>

          <button
          type="button" 
            class="btn btn-primary btn-sm ms-2" 
            data-tippy-content="refresh" 
            @click="refreshData"
><font-awesome-icon icon="sync-alt"/></button>
          
          <SearchBox 
            class="ms-3"
            tool-tip="search by enrol profile fields"
            @search="newSearchText($event)" 
            :search-term="searchTerm"
/>

          <PageNav
v-show="showPageNav" 
            :num-pages="pageList.length"
            ref="pageNav"
            @page-change="changePage($event)"
/>
        </div>

        <div v-if="showRows">
          <EnrolProfileRow 
            v-for="(enrolProfile, index) in curPage.enrolProfileList" 
            
            :key="enrolProfile.enrolId" 
            :enrol-profile="enrolProfile" 
            :used-enrol-id-list="usedEnrolIdList"
            :index="index"

            @edit="changeStatus('edit', $event)"
            @delete="changeStatus('delete', $event)"
/>
        </div> 
      </div>

      <div class="mobile-view">
        <div class="search-container-phone mb-2">

          <PageNav
v-show="showPageNav" 
            :num-pages="pageList.length"
            ref="pageNav"
            class="m-1"
            @page-change="changePage($event)"
/>

          <button
          type="button" 
            class="btn btn-primary btn-sm m-1" 
            @click="refreshData"
><font-awesome-icon icon="sync-alt"/> refresh</button>
          
          <SearchBox 
            class="m-1"
            tool-tip="search by enrol profile fields"
            @search="newSearchText($event)" 
            :search-term="searchTerm"
/>
          
        </div>
        <div v-if="showRows">
          <EnrolProfileRowMobile
            v-for="(enrolProfile, index) in curPage.enrolProfileList" 
            
            :key="enrolProfile.enrolId" 
            :enrol-profile="enrolProfile" 
            :used-enrol-id-list="usedEnrolIdList"
            :index="index"

            @edit="changeStatus('edit', $event)"
            @delete="changeStatus('delete', $event)"
/>
        </div> 
      </div>


      <div v-if="showNoMatches" class="empty-search">
        <p><font-awesome-icon icon="times"/> No enrol profile matches your search: '{{searchTerm}}'</p>
      </div>

    </div>
    
    <div v-if="status === 'create'">
      <CreateEnrolProfile @cancel="changeStatus('view-all')" :enrol-profile-id-list="usedEnrolIdList"/>
    </div>

    <div v-if="status === 'edit'">
      <EditEnrolProfile @cancel="changeStatus('view-all')" :enrol-profile="selectedEnrolProfile"/>
    </div>

    <div v-if="status === 'delete'">
      <DeleteEnrolProfile @cancel="changeStatus('view-all')" :enrol-profile="selectedEnrolProfile"/>
    </div>
    

    <Loading v-if="httpBusy"/>

    <Error :label="error"/>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ApiService, curApiService, EnrolProfilePage } from '@/services/ApiService';
import { curPref } from '@/services/Preferences';
import Error from '@/components/Error.vue';
import Loading from '@/components/Loading.vue';
import SearchBox from '@/components/SearchBox.vue';
import PageNav from '@/components/PageNav.vue';
import EnrolProfileRow from '@/components/admin/EnrolProfileRow.vue';
import EnrolProfileRowMobile from '@/components/admin/EnrolProfileRowMobile.vue';
import CreateEnrolProfile from '@/components/admin/CreateEnrolProfile.vue';
import DeleteEnrolProfile from '@/components/admin/DeleteEnrolProfile.vue';
import EditEnrolProfile from '@/components/admin/EditEnrolProfile.vue';
import { EnrolProfile, Enterprise } from '@/types';
import _ from 'lodash';

const route = useRoute();
const router = useRouter();

const status = ref<'view-all'|'create'|'edit'|'delete'>('view-all');
const selectedEnrolProfile = ref<EnrolProfile>({} as EnrolProfile);

const httpBusy = ref<boolean>(false);
const error = ref<string>('');
const pageList = ref<EnrolProfilePage[]>([]);
const curPage = ref<EnrolProfilePage>({} as EnrolProfilePage);
const searchTerm = ref<string>(curPref.retrieve('noc-enrol-profile-list-search-term', ''));
const enterpriseList = ref<Enterprise[]>([]);

const pageNav = ref<InstanceType<typeof PageNav>>();

// services
const apiService: ApiService = curApiService;

onMounted(() => {
  if(route.params.searchTerm)
    searchTerm.value = route.params.searchTerm as string;

  refreshData();
});

const refreshData = async () => {
  httpBusy.value = true;

  const entReq = apiService.getEnterprises({});
  const enrolReq = apiService.getEnrolProfilesPaginated({searchTerm: searchTerm.value}); 

  const [entReply, enrolReply] = await Promise.all([entReq, enrolReq]);

  httpBusy.value = false;

  if(entReply.httpCode === 403 || enrolReply.httpCode === 403)
    return router.push({name:'login'});
  
  if(entReply.errorMsg)
    return error.value = `Unable to load enterprises because ${entReply.errorMsg}`;
  
  if(enrolReply.errorMsg)
    return error.value = `Unable to load enrolment profiles because ${enrolReply.errorMsg}`;
  
  enterpriseList.value = entReply.entList!;
  pageList.value = enrolReply.pageList!;

  // we set the current page
  if(pageList.value.length > 0) {
    // we call the reset on the component
    pageNav.value?.reset();
    curPage.value = pageList.value[0];
  }
};

const newSearchText = (newSearchText: string) => {
  curPref.store('noc-enrol-profile-list-search-term', newSearchText);
  searchTerm.value = newSearchText;
  refreshData();
};

const changeStatus = (newStatus: 'view-all'|'create'|'edit'|'delete', selectedEnrolProfileParam?: EnrolProfile) => {
  status.value = newStatus;

  if(newStatus === 'view-all')
    refreshData();

  if(selectedEnrolProfileParam)
    selectedEnrolProfile.value = selectedEnrolProfileParam;
};

const showPageNav = computed<boolean>(() => {
  return pageList.value.length > 0 && !httpBusy.value;
});

const showRows = computed<boolean>(() => {
  return pageList.value.length > 0 && !httpBusy.value;
});

const showNoMatches = computed<boolean>(() => {
  return !httpBusy.value && pageList.value.length === 0 && searchTerm.value !== '' && error.value === '';
});

const usedEnrolIdList = computed<string[]>(() => {
  return _.uniq(enterpriseList.value
    .map(ent => ent.nwServer && ent.nwServer.actility && ent.nwServer.actility.enrolId ? ent.nwServer.actility.enrolId : 'default'));
});

const changePage = (newPageNum: number) => {
  curPage.value = pageList.value[newPageNum];
};
</script>


<style scoped lang="scss">

.search-container {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
}

.search-container-phone {
  width: 90%;
  margin-right: auto;
  margin-left: auto;
  display: flex;
  flex-direction: column-reverse;
  align-items: stretch;

}

/* Responsive visibility */
.desktop-view {
  display: block;
}

.mobile-view {
  display: none;
}

@media (max-width: 767px) {
  .desktop-view {
    display: none;
  }
  
  .mobile-view {
    display: block;
  }
}

</style>